import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { UserPlus, UserCheck, AlertCircle, Sparkles, MessageCircle, ShoppingBag, PartyPopper } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useSellerRef } from "@/context/SellerRefContext";
import { SELLERS, findSellerByCode, buildNewCustomerNotice } from "@/lib/sellers";
import { toast } from "sonner";

export default function Register() {
  const { user, customerRegister } = useAuth();
  const { refCode } = useSellerRef();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    cuit: "",
    password: "",
    seller_code: refCode || "",
  });
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const navigate = useNavigate();

  if (created) return <RegisterSuccess customer={created.customer} seller={created.seller} onContinue={() => navigate("/shop")} />;
  if (user) return <Navigate to="/shop" replace />;

  const seller = findSellerByCode(form.seller_code);
  const codeProvided = form.seller_code.trim().length > 0;
  const sellerError = codeProvided && !seller;

  const submit = async (e) => {
    e.preventDefault();
    if (!seller) { toast.error("Ingresá un código de vendedor válido"); return; }
    if (form.password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    setLoading(true);
    const res = await customerRegister(form);
    setLoading(false);
    if (res.ok) { setCreated({ customer: form, seller }); toast.success("¡Cuenta creada! Tenés 5% OFF de bienvenida en tu primer pedido."); }
    else toast.error(res.error || "Error al crear la cuenta");
  };

  return (
    <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-4 py-12 hero-pattern" data-testid="register-page">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-xl w-full max-w-md p-8">
        <div className="flex justify-center"><Logo size="md" /></div>
        <div className="mt-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-full mb-3">
            <UserPlus className="w-5 h-5 text-blue-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold">Creá tu cuenta</h1>
          <p className="text-sm text-slate-500 mt-1">Para ver precios y comprar online</p>
          <div className="mt-3 inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-1.5 rounded-full text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5" /> ¡5% OFF de regalo en tu primera compra!
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700">Nombre (empresa / supermercado) *</label>
            <input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: Supermercado El Norte"
              className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="register-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Email *</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="register-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Teléfono</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="register-phone" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Dirección *</label>
            <input required minLength={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Calle y número"
              className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="register-address" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Ciudad *</label>
              <input required minLength={2} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="register-city" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">CUIT *</label>
              <input required minLength={2} value={form.cuit} onChange={(e) => setForm({ ...form, cuit: e.target.value })}
                placeholder="20-12345678-9"
                className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="register-cuit" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Contraseña * <span className="text-xs text-slate-400">(min. 6 caracteres)</span></label>
            <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="register-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Código de vendedor *</label>
            <div className="relative mt-1">
              <input required maxLength={3} value={form.seller_code} onChange={(e) => setForm({ ...form, seller_code: e.target.value })}
                placeholder="Ej: 03"
                className={`w-full px-4 py-2.5 pr-10 rounded-lg border outline-none ${sellerError ? "border-red-300 bg-red-50" : seller ? "border-green-300 bg-green-50" : "border-slate-200 focus:border-blue-400"}`}
                data-testid="register-seller-code" />
              {seller && <UserCheck className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />}
              {sellerError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />}
            </div>
            {seller && <p className="text-xs text-green-700 mt-1">✓ Vendedor: <strong>{seller.name}</strong></p>}
            {sellerError && <p className="text-xs text-red-600 mt-1">Código no encontrado. Válidos: {SELLERS.map((s) => s.code).join(", ")}</p>}
            {!codeProvided && <p className="text-xs text-slate-400 mt-1">Pedile el código a tu vendedor o ingresá por su link personal.</p>}
          </div>

          <button type="submit" disabled={loading || !seller} className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-3 font-semibold transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed mt-2" data-testid="register-submit">
            {loading ? "Creando..." : "Crear cuenta"}
          </button>
        </form>
        <p className="text-sm text-slate-600 text-center mt-5">
          ¿Ya tenés cuenta? <Link to="/login" className="text-blue-600 hover:text-blue-800 font-semibold" data-testid="goto-login">Iniciá sesión</Link>
        </p>
      </div>
    </div>
  );
}

function RegisterSuccess({ customer, seller, onContinue }) {
  const notice = buildNewCustomerNotice(customer, seller);
  return (
    <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-4 py-12 hero-pattern" data-testid="register-success">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden">
        <div className="h-3 bg-gradient-to-r from-blue-600 via-green-500 to-red-500" />
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 rounded-full mb-4">
            <PartyPopper className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold">¡Cuenta creada, {customer.name.split(" ")[0]}!</h1>
          <p className="text-sm text-slate-500 mt-2">
            Te atiende <strong className="text-blue-700">{seller.name}</strong> (cód. {seller.code}). Avisale que ya te registraste así te contacta y coordinan tu primer pedido.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-1.5 rounded-full text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5" /> Tenés 5% OFF en tu primera compra
          </div>
          <div className="mt-6 space-y-3">
            <a
              href={notice.url}
              target="_blank" rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-full px-6 py-3 font-semibold transition-colors"
              data-testid="notify-seller-btn"
            >
              <MessageCircle className="w-4 h-4" /> Avisar a mi vendedor por WhatsApp
            </a>
            <button onClick={onContinue} className="w-full inline-flex items-center justify-center gap-2 border-2 border-slate-200 hover:border-blue-500 hover:text-blue-700 text-slate-700 rounded-full px-6 py-3 font-semibold transition-colors" data-testid="register-continue-btn">
              <ShoppingBag className="w-4 h-4" /> Ir a la tienda
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-5">El mensaje ya va armado con tus datos; solo tenés que tocar Enviar.</p>
        </div>
      </div>
    </div>
  );
}
